import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { createTablet, updateTablet, useTablets } from '@/lib/api/tablet';
import { DEFAULT_SINGLE_EVENT_KEY, Tablet } from '@/lib/schema/tablet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const TabletFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  location: z.string().min(1, 'Location is required').max(100),
  // Kept as text in the form; converted to a number on save.
  suiteId: z
    .string()
    .trim()
    .regex(/^\d*$/, 'Suite ID must be a whole number')
    .max(10),
  singleEventKey: z
    .string()
    .trim()
    .min(1, 'Single event key is required')
    .max(50)
    .refine((v) => !v.includes('_'), 'Underscores are not allowed'),
  pin: z
    .string()
    .trim()
    .regex(/^\d*$/, 'PIN must be digits only')
    .max(10),
  qrEnabled: z.boolean(),
});
type TabletFormValues = z.infer<typeof TabletFormSchema>;

type TabletFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // null = create a new tablet
  tablet: Tablet | null;
};

export function TabletFormDialog({
  open,
  onOpenChange,
  tablet,
}: TabletFormDialogProps) {
  const [tablets] = useTablets();

  const form = useForm<TabletFormValues>({
    resolver: zodResolver(TabletFormSchema),
    defaultValues: {
      name: '',
      location: '',
      suiteId: '',
      singleEventKey: DEFAULT_SINGLE_EVENT_KEY,
      pin: '',
      qrEnabled: true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: tablet?.name || '',
        location: tablet?.location || '',
        suiteId:
          tablet?.suiteId !== null && tablet?.suiteId !== undefined
            ? String(tablet.suiteId)
            : '',
        singleEventKey: tablet?.singleEventKey || DEFAULT_SINGLE_EVENT_KEY,
        pin: tablet?.pin || '',
        qrEnabled: tablet ? tablet.qrEnabled !== false : true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tablet]);

  const mutation = useMutation({
    mutationFn: (value: TabletFormValues) => {
      const data = {
        name: value.name,
        location: value.location,
        suiteId: value.suiteId === '' ? null : Number(value.suiteId),
        singleEventKey: value.singleEventKey,
        pin: value.pin === '' ? null : value.pin,
        qrEnabled: value.qrEnabled,
      };
      return tablet?.id
        ? updateTablet(tablet.id, data)
        : createTablet({ ...data, groupId: null });
    },
    onSuccess: () => {
      toast.success(tablet ? 'Tablet updated' : 'Tablet created');
      onOpenChange(false);
    },
    onError: (error) => {
      console.error('Error saving tablet:', error);
      toast.error('Failed to save tablet');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tablet ? 'Edit tablet' : 'New tablet'}</DialogTitle>
          <DialogDescription>
            The name must match the Vision Edge Luxury Suite name exactly.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((value) => {
              const nameLower = value.name.trim().toLowerCase();
              if (
                tablets?.some(
                  (t) => t.nameLower === nameLower && t.id !== tablet?.id
                )
              ) {
                form.setError('name', {
                  type: 'manual',
                  message: 'Name must be unique',
                });
                return;
              }
              if (value.suiteId !== '') {
                const suiteId = Number(value.suiteId);
                if (
                  tablets?.some(
                    (t) => t.suiteId === suiteId && t.id !== tablet?.id
                  )
                ) {
                  form.setError('suiteId', {
                    type: 'manual',
                    message: 'Suite ID is already used by another tablet',
                  });
                  return;
                }
              }
              mutation.mutate(value);
            })}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      inError={!!form.formState.errors.name}
                      placeholder="Suite_M42C"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message || ''}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      inError={!!form.formState.errors.location}
                      placeholder="Suite M42C"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message || ''}</FormMessage>
                </FormItem>
              )}
            />
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="suiteId"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Suite ID</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        inputMode="numeric"
                        inError={!!form.formState.errors.suiteId}
                        placeholder="101"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>
                      {fieldState.error?.message || ''}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="singleEventKey"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Single event key</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        inError={!!form.formState.errors.singleEventKey}
                        placeholder={DEFAULT_SINGLE_EVENT_KEY}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>
                      {fieldState.error?.message || ''}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pin"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>PIN</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        inputMode="numeric"
                        inError={!!form.formState.errors.pin}
                        placeholder="00121"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>
                      {fieldState.error?.message || ''}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="qrEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>QR code</FormLabel>
                    <FormDescription>
                      Whether this tablet gets a QR code. Independent of the
                      group ON/OFF that controls the wall tablet.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormDescription>
              The QR code is the CRC32 (hex) of Suite ID_Single event key_Event key.
              The event key is set once for all tablets on the Tablets page.
            </FormDescription>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {tablet
                  ? mutation.isPending
                    ? 'Updating...'
                    : 'Update tablet'
                  : mutation.isPending
                    ? 'Creating...'
                    : 'Create tablet'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
